import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowDownWideNarrow, ListOrdered } from "lucide-react";
import { Section, LineStatus, Missing, inr, qtyFmt } from "./costingUi";

const HEAD = ["#", "Ingredient ID", "Ingredient", "Recipe Qty", "Unit", "Purchase Rate", "Rate Unit",
  "Pack Size", "Normalized Qty", "Line Cost", "% of Ingr. Cost", "Status"];

function topIds(lines) {
  return new Set(lines.filter((l) => l.lineCost > 0).sort((a, b) => b.lineCost - a.lineCost).slice(0, 3).map((l) => l.seq));
}

function Row({ l, top }) {
  const bad = l.status === "UNIT_REVIEW" || l.status === "PRICE_MISSING";
  return (
    <tr data-testid={`cost-line-${l.seq}`} className={`border-b border-stone-100 ${top ? "bg-amber-50/70" : ""} ${bad ? "bg-rose-50/50" : ""}`}>
      <td className="px-2 py-2 text-stone-400">{l.seq}</td>
      <td className="px-2 py-2 font-mono text-[11px] text-stone-500">{l.ingredientCode || "—"}</td>
      <td className="px-2 py-2 font-medium text-stone-800">{l.name}{top && <span className="ml-1.5 rounded bg-amber-200 px-1 text-[9px] font-bold text-amber-900">TOP COST</span>}</td>
      <td className="px-2 py-2 text-right font-mono">{qtyFmt(l.qty)}</td>
      <td className="px-2 py-2">{l.unit}</td>
      <td className="px-2 py-2 text-right font-mono">{l.purchaseRate != null && l.status !== "PRICE_MISSING" ? inr(l.purchaseRate, 4) : "—"}</td>
      <td className="px-2 py-2">{l.rateUnit ? `per ${l.rateUnit}` : "—"}</td>
      <td className="px-2 py-2 font-mono text-[11px] text-stone-500">
        {l.packSize ? `${qtyFmt(l.packSize)} ${l.packUnit || ""}${l.purchasePrice ? ` @ ${inr(l.purchasePrice)}` : ""}` : "—"}
      </td>
      <td className="px-2 py-2 text-right font-mono">{l.normalizedQty != null ? `${qtyFmt(l.normalizedQty, 4)} ${l.rateUnit}` : "—"}</td>
      <td className="px-2 py-2 text-right font-mono font-semibold" data-testid={`cost-line-${l.seq}-cost`}>
        {l.lineCost != null ? inr(l.lineCost) : <Missing text="—" />}
      </td>
      <td className="px-2 py-2 text-right font-mono" data-testid={`cost-line-${l.seq}-pct`}>
        {l.pctOfIngredientCost != null ? `${l.pctOfIngredientCost.toFixed(2)}%` : "—"}
      </td>
      <td className="px-2 py-2"><LineStatus status={l.status} testId={`cost-line-${l.seq}-status`} /></td>
    </tr>
  );
}

export function IngredientCostTable({ data }) {
  const [sort, setSort] = useState("cost");
  const { lines, totals: t } = data;
  const top = useMemo(() => topIds(lines), [lines]);
  const rows = useMemo(() => (sort === "cost"
    ? [...lines].sort((a, b) => (b.lineCost ?? -1) - (a.lineCost ?? -1) || a.seq - b.seq) : lines), [lines, sort]);
  const toggle = (
    <div className="flex gap-1.5">
      <Button size="sm" variant={sort === "cost" ? "default" : "outline"} className="h-7 gap-1 text-xs" data-testid="sort-by-cost" onClick={() => setSort("cost")}>
        <ArrowDownWideNarrow className="h-3.5 w-3.5" /> Highest cost
      </Button>
      <Button size="sm" variant={sort === "seq" ? "default" : "outline"} className="h-7 gap-1 text-xs" data-testid="sort-by-sequence" onClick={() => setSort("seq")}>
        <ListOrdered className="h-3.5 w-3.5" /> Recipe sequence
      </Button>
    </div>
  );
  return (
    <Section title="Ingredient Costing (Current Master Prices)" right={toggle} testId="ingredient-cost-table">
      {!t.complete && (
        <div data-testid="ingredient-incomplete-warning" className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">
          {t.unresolvedCount} line(s) need review (UNIT_REVIEW / missing price). They are excluded from the subtotal — totals and percentages are incomplete.
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="border-b border-stone-200 text-left text-[10px] uppercase tracking-wider text-stone-500">
            {HEAD.map((h) => <th key={h} className="whitespace-nowrap px-2 py-2 font-semibold">{h}</th>)}
          </tr></thead>
          <tbody>{rows.map((l) => <Row key={l.seq} l={l} top={top.has(l.seq)} />)}</tbody>
          <tfoot>
            <tr className="border-t-2 border-stone-300 bg-stone-50 font-semibold" data-testid="ingredient-total-row">
              <td colSpan={3} className="px-2 py-2.5">TOTAL ({t.lineCount} lines)</td>
              <td colSpan={6} className="px-2 py-2.5 font-mono text-[11px] text-stone-600">
                Weight: {t.inputWeightKg != null ? `${qtyFmt(t.inputWeightKg)} kg` : "—"} · Volume: {t.inputVolumeL != null ? `${qtyFmt(t.inputVolumeL)} L` : "—"}
                {t.inputCountPcs ? ` · Count: ${qtyFmt(t.inputCountPcs)} pcs` : ""}
              </td>
              <td className="px-2 py-2.5 text-right font-mono" data-testid="ingredient-subtotal">{inr(t.ingredientCost)}</td>
              <td className="px-2 py-2.5 text-right font-mono">{t.ingredientCost > 0 ? "100%" : "—"}</td>
              <td className="whitespace-nowrap px-2 py-2.5" data-testid="ingredient-unresolved-count">{t.unresolvedCount ? `${t.unresolvedCount} unresolved` : "All resolved"}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="mt-2 text-[11px] text-stone-500">Sorting is display-only; the stored recipe sequence is unchanged. Highlighted rows are the top 3 cost drivers.</p>
    </Section>
  );
}
